import React, { Component } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { parseFullName } from 'parse-full-name';
import {
  message,
  Form,
  Input,
  Checkbox,
  Select,
  AutoComplete,
  Tooltip,
  Button,
  Table,
  Typography,
  Alert,
  Divider,
  Progress,
} from 'antd';
import DatePicker from '../DatePicker';
import { InfoCircleOutlined, SearchOutlined } from '@ant-design/icons';
import ModalWrapper from './ModalWrapper';
import { extractReplyTiffWarnings, showReplyTiffWarningModal } from '../chartActions/replyTiffWarning';
import ReferringProviderAutocomplete from '../autocomplete/ReferringProviderAutocomplete';
import { taskCreatedByStamp, buildTaskTemplateNotes } from '../../utils/taskTemplateNotes';
import dedupeDocumentCategories from '../../utils/dedupeDocumentCategories';
import { captureAnalytics } from '../../actions/preload';
import { ARCHIVE_AFTER_RECORD_ACTIONS_FEATURE, hasFeatureInLocalStorage, verifyFeatureAccess } from '../../global/featureFlags';
import {
  caretrackerEmr,
  caretrackerAccessOrg,
  categoryRequiredEmrs,
  caretrackerProviders,
  resultsTable,
  priorityOptions,
  taskOptions,
  taskDetailsOptions,
  authorizationTypeOptions,
} from './constants';
import { PREPOST_PRACTICE_OPTIONS, PREPOST_TENNESSEE_PRACTICE } from './prePostPractice';

const EMA_RESULTS_METADATA_ORG_ID = 4799;

export class AutoUpload extends Component {
  constructor(props) {
    super(props);
    this.state = {
      fileName: props.modalType.data.fileName,
      receiverType: props.modalType.data.receiverType,
      archiveAfterUpload: false,
      documentCategory: props.modalType.data.documentCategory || '',
      documentCategories: [],
      providerName: props.modalType.data.providerName,
      createTaskAfterUpload: localStorage.createTaskAfterUpload,
      patientName: this.props.modalType.data.patientName || '',
      patientFirstName: this.props.modalType.data.patientFirstName || '',
      patientMiddleName: this.props.modalType.data.patientMiddleName || '',
      patientLastName: this.props.modalType.data.patientLastName || '',
      apiPatientsList: [],
      searchType: 'name',
      apiCredentials: this.props.modalType.data.apiCredentials,
      patientDOB: this.props.modalType.data.patientDOB || '',
      patientMrn: '',
      selectedEmrPatientId: null,
      assignTo: undefined,
      searchedTemplates: null,
      emaTaskTemplates: null,
      searchedNames: null,
      allProviderNames: [],
      task: '',
      searchedTasks: '',
      taskDetails: '',
      dueDate: '',
      dateOfService: props.modalType.data.dateOfService
        ? moment(props.modalType.data.dateOfService, 'YYYY-MM-DD').format('MM/DD/YYYY')
        : '',
      documentType: '',
      careTrackerProvider: '',
      taskDetailsSearchOptions: null,
      notes: taskCreatedByStamp(),
      priority: 'Normal',
      noTaskCreation: this.props.modalType.data.noTaskCreation,
      practiceName: null,
      apiPatientFound: false,
      searchedPatientDOB: '',
      searchedPatientMRN: '',
      searchedPatientFirstName: '',
      searchedPatientMiddleName: '',
      searchedPatientLastName: '',
      value: '',
      open: false,
      loading: false,
      searchByDOB: false,
      searchedDOBResults: [],
      facilityName: '',
      facilityNames: [],
      searchedFacilities: [],
      icd10Codes: props.modalType.data.icdCodes ? (Array.isArray(props.modalType.data.icdCodes) ? props.modalType.data.icdCodes : props.modalType.data.icdCodes.split(',').map((code) => code.trim())) : [],
      marketingRep: props.modalType.data.marketingRep || '',
      orderingProvider: '',
      showNameMismatchWarning: false,
      initialParsedName: '',
      // Initialize authorization fields from AI-extracted data
      // AI dates come in YYYY-MM-DD format, convert to MM/DD/YYYY for DatePicker
      // EMA users always start with authorization checkbox unchecked
      addAuthorization: localStorage.m8_auto_upload_emr === 'EMA' ? false : !!(props.modalType.data.authorizationNumber),
      authorizationType: props.modalType.data.authorizationNumber ? 'Authorization' : '',
      authorizationNumber: props.modalType.data.authorizationNumber || '',
      authorizationDescription: props.modalType.data.authorizationDescription || '',
      authorizationActiveStatus: true,
      authorizationUnlimitedVisits: !props.modalType.data.authorizationNumberOfVisits && !!(props.modalType.data.authorizationFromDate && props.modalType.data.authorizationToDate),
      authorizationNumberOfVisits: props.modalType.data.authorizationNumberOfVisits ? String(props.modalType.data.authorizationNumberOfVisits) : '',
      authorizationFromDate: props.modalType.data.authorizationFromDate ? moment(props.modalType.data.authorizationFromDate, 'YYYY-MM-DD').format('MM/DD/YYYY') : '',
      authorizationToDate: props.modalType.data.authorizationToDate ? moment(props.modalType.data.authorizationToDate, 'YYYY-MM-DD').format('MM/DD/YYYY') : '',
      authorizationNote: '',
      authorizationReferringProvider: '',
      canExtractAuthorization: false,
    };

    // Calculate initial name mismatch - this is determined once and never changes
    const normalizeSpaces = (str) => str ? str.trim().replace(/\s+/g, ' ') : '';
    const initialConstructedName = [
      this.props.modalType.data.patientFirstName,
      this.props.modalType.data.patientMiddleName,
      this.props.modalType.data.patientLastName,
    ].filter(Boolean).join(' ').trim();
    const normalizedConstructedName = normalizeSpaces(initialConstructedName);
    const normalizedOriginalName = normalizeSpaces(this.props.modalType.data.patientName);

    this.state.initialParsedName = initialConstructedName;
    this.state.showNameMismatchWarning = this.props.modalType.data.patientName &&
                                          normalizedConstructedName &&
                                          normalizedOriginalName &&
                                          normalizedConstructedName !== normalizedOriginalName;

    this.autoCompleteRef = React.createRef();
    this.state.searchedTasks = taskOptions;
    this.state.taskDetailsSearchOptions = taskDetailsOptions;
    this.formRef = React.createRef();
  }

  async componentDidMount() {
    const {
      getDocumentCategories,
      getEmaTaskProviders,
      getKareoTaskProviders,
      getTaskTemplatesByQuery,
      getMarketingRepresentatives,
      modalType: { data: { sendToken } },
    } = this.props;
    const autoUploadEmr = localStorage.m8_auto_upload_emr;

    await getDocumentCategories();

    // Fetch appropriate providers based on EMR type
    if (autoUploadEmr === 'Kareo') {
      await getKareoTaskProviders(sendToken);
    } else {
      await getEmaTaskProviders(sendToken);
    }

    await getMarketingRepresentatives();

    // Get provider names from the appropriate source
    let allProviders = [];
    if (autoUploadEmr === 'Kareo') {
      // Kareo providers are objects with {name, id}
      allProviders = this.props.allKareoTaskProviders || [];
    } else {
      // EMA providers are objects with {name, is_provider}
      allProviders = this.props.allEmaTaskProviders || [];
    }

    // For display, extract names
    const providerNames = allProviders.map((p) => {
      return typeof p === 'string' ? p : p?.name;
    }).filter(Boolean);

    this.setState({
      fileName: this.props.modalType.data.fileName,
      providerName: this.props.modalType.data.providerName,
      searchedNames: providerNames,
      allProviderNames: providerNames,
      documentCategories: this.props.allDocumentCategories.categories,
    }, () => {
      this.prefillOrderingProvider(providerNames, this.props.modalType.data.documentCategory);
    });
    if (providerNames.length === 1) {
      let newAssignTo = providerNames[0];
      if (autoUploadEmr === 'EMA') {
        // Set assign to as array
        newAssignTo = [newAssignTo];
      }
      this.setState({ assignTo: newAssignTo });
    }
    const query = `?email=${localStorage.getItem('m8_uid')}`;
    await getTaskTemplatesByQuery(query);
    this.setState({
      emaTaskTemplates: this.props.allEmaTaskTemplates.ema_task_templates,
      searchedTemplates: this.props.allEmaTaskTemplates.ema_task_templates,
    });
    const { patientName, patientFirstName, patientLastName } = this.state;
    if (!(patientFirstName || patientLastName) && patientName) {
      // Handle edge case where the patient_name has 2 surnames (this is common for Spanish names)
      if (patientName.includes(',') && patientName.split(',')[0].trim().includes(' ') && patientName.split(',')[0].trim().split(' ').length > 1) {
        const parsedNames = this.parseName(patientName);
        this.setState({
          patientFirstName: parsedNames.firstName,
          patientLastName: parsedNames.lastName,
        });
      } else {
        const parsedName = parseFullName(patientName);
        this.setState({
          patientFirstName: parsedName.first,
          patientMiddleName: parsedName.middle,
          patientLastName: parsedName.last,
        });
      }
    }
    const orgName = localStorage.m8_org;
    if (orgName === 'PrePost+') {
      this.setState({ practiceName: PREPOST_TENNESSEE_PRACTICE });
    }

    if (autoUploadEmr === 'EMA' && this.state.apiCredentials) {
      try {
        const response = await axios.get('/api/v1/emr_facilities');
        const res = await response.data;

        this.setState({
          facilityNames: res.facilities,
          searchedFacilities: res.facilities,
        });
      } catch (error) {
        console.error('Error fetching facilities:', error);
      }
    }

    // Check feature flag for authorization extraction
    const canExtractAuthorization = await verifyFeatureAccess('extract_authorization_details');
    this.setState({ canExtractAuthorization });
  }

  isResultsCategory = (category = '') => (category || '').startsWith('Results - ');

  isScopedManualResultsFlow = (category = this.state.documentCategory) => {
    const autoUploadEmr = localStorage.getItem('m8_auto_upload_emr');
    const modalData = this.props.modalType?.data || {};
    return autoUploadEmr === 'EMA'
      && Number(modalData.currentOrganizationId) === EMA_RESULTS_METADATA_ORG_ID
      && this.isResultsCategory(category);
  };

  // EMA exposes an ordering-provider dropdown for any document filed into its
  // Results tab, which every "Results - *" category routes to.
  shouldShowOrderingProviderField = (category = this.state.documentCategory) => {
    const autoUploadEmr = localStorage.getItem('m8_auto_upload_emr');
    if (autoUploadEmr !== 'EMA') return false;
    return (category || '').toLowerCase().startsWith('results - ');
  };

  shouldShowPerformedDateField = (category = this.state.documentCategory) => (
    this.isScopedManualResultsFlow(category)
  );

  parseProviderNameParts = (name) => {
    const trimmedName = (name || '').trim();
    if (!trimmedName) {
      return { first: '', last: '' };
    }

    if (trimmedName.includes(',')) {
      const [lastName = '', firstName = ''] = trimmedName.split(',').map((part) => part.trim());
      return {
        first: firstName.split(/\s+/)[0].toLowerCase(),
        last: lastName.toLowerCase(),
      };
    }

    const parsedName = parseFullName(trimmedName);
    const fallbackParts = trimmedName.split(/\s+/);
    return {
      first: (parsedName.first || fallbackParts[0] || '').toLowerCase(),
      last: (parsedName.last || fallbackParts[fallbackParts.length - 1] || '').toLowerCase(),
    };
  };

  findMatchingOrderingProvider = (providerName, providerNames = []) => {
    if (!providerName || !providerNames.length) return null;

    const normalizedProviderName = providerName.trim().toLowerCase();
    const exactMatch = providerNames.find((name) => name.toLowerCase() === normalizedProviderName);
    if (exactMatch) return exactMatch;

    const target = this.parseProviderNameParts(providerName);
    if (!target.last) return null;

    const matches = providerNames.filter((name) => {
      const candidate = this.parseProviderNameParts(name);
      return candidate.last === target.last
        && (!target.first || candidate.first === target.first);
    });

    return matches.length === 1 ? matches[0] : null;
  };

  prefillOrderingProvider = (
    providerNames = this.getOrderingProviderNames(),
    documentCategory = this.state.documentCategory,
  ) => {
    if (
      !this.shouldShowOrderingProviderField(documentCategory)
      || !this.isScopedManualResultsFlow(documentCategory)
      || this.state.orderingProvider
      || !this.state.providerName
    ) {
      return;
    }

    const matchedProvider = this.findMatchingOrderingProvider(this.state.providerName, providerNames);
    if (matchedProvider) {
      this.setState({ orderingProvider: matchedProvider }, () => {
        if (this.formRef.current) {
          this.formRef.current.setFieldsValue({
            orderingProvider: matchedProvider,
          });
        }
      });
    }
  };

  // Helper to check if we should clear patient selection when fields are modified
  shouldClearPatientSelection = () => {
    const autoUploadEmr = localStorage.getItem('m8_auto_upload_emr');
    return this.state.selectedEmrPatientId !== null && autoUploadEmr === 'Kareo';
  }

  handleFieldChange = (e) => {
    const { name, value } = e.target;
    const isPatientField = ['patientFirstName', 'patientMiddleName', 'patientLastName'].includes(name);

    // If user modifies patient fields after selecting from search (for Tebra),
    // clear the selectedEmrPatientId to force a new search by name/DOB
    if (isPatientField && this.shouldClearPatientSelection()) {
      this.setState({
        [name]: value,
        selectedEmrPatientId: null,
      });
    } else {
      this.setState({
        [name]: value,
      });
    }
  }

  handleNotesChange = (e) => {
    this.setState({ notes: e.target.value });
  }

  handleSelectionChange = (e, variable) => {
    this.setState({ [variable]: e });
  };

  handleDocumentCategoryChange = (documentCategory) => {
    this.setState({ documentCategory }, () => {
      this.prefillOrderingProvider();
    });
  };

  handleFacilitySearch = (value) => {
    if (value) {
      const filtered = this.state.facilityNames.filter(facility =>
        facility.name.toLowerCase().includes(value.toLowerCase())
      );
      this.setState({ searchedFacilities: filtered });
    } else {
      this.setState({ searchedFacilities: this.state.facilityNames });
    }
  };

  handleFacilityChange = (value) => {
    if (!value) {
      this.setState({ facilityName: '', facilityId: null });
      return;
    }
    const selectedFacility = this.state.searchedFacilities.find(facility => facility.name === value);
    this.setState({ facilityName: selectedFacility.name, facilityId: selectedFacility.id });
  };

  handleCreateTaskAfterUpload = async (e) => {
    if (!localStorage.createTaskAfterUpload) {
      localStorage.setItem('createTaskAfterUpload', true);
    } else {
      localStorage.removeItem('createTaskAfterUpload');
    }
    this.setState({ createTaskAfterUpload: e.target.checked, archiveAfterUpload: e.target.checked ? false : this.state.archiveAfterUpload }, () => {
      requestAnimationFrame(() => {
        const taskTemplatesSection = document.querySelector('[id="taskTemplates"]');
        if (taskTemplatesSection) {
          taskTemplatesSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    });
  }

  handleDateChange = (_, dateString) => {
    // If user modifies DOB after selecting from search (for Tebra),
    // clear the selectedEmrPatientId to force a new search by name/DOB
    if (this.shouldClearPatientSelection()) {
      this.setState({
        patientDOB: dateString,
        selectedEmrPatientId: null,
      });
    } else {
      this.setState({ patientDOB: dateString });
    }
  };

  handleDueDateChange = (_, dateString) => {
    this.setState({ dueDate: dateString });
  };

  handleDateOfServiceChange = (_, dateString) => {
    this.setState({ dateOfService: dateString });
  };

  handleAssignToSearch = (e) => {
    const allNames = this.state.allProviderNames || [];

    if (e) {
      const filtered = allNames.filter((name) => name.toLowerCase().includes(e.toLowerCase()));
      this.setState({ searchedNames: filtered });
    } else {
      this.setState({ searchedNames: allNames });
    }
  };

  handleTaskSearch = (e) => {
    if (e) {
      const filtered = this.taskOptions.filter(task => task.toLowerCase().includes(e.toLowerCase()));
      this.setState({ searchedTasks: filtered });
    } else {
      this.setState({ searchedTasks: this.taskOptions });
    }
  };

  handleSearchTaskDetails = (e) => {
    this.setState({ taskDetails: e });
    const filteredOptions = taskDetailsOptions.filter(option => (
      option.toLowerCase().includes(e.toLowerCase())));
    this.setState({ taskDetailsSearchOptions: filteredOptions });
  };

  handleTaskTemplateChange = (e) => {
    if (this.isKareoEmr()) {
      captureAnalytics('tebra_note_template_selection_changed', {
        action: e ? 'selected' : 'cleared',
        workflow: 'send_to_chart',
        modal_variant: 'legacy',
      });
    }
    // if user clears out selection, we will reload the list of templates to choose from.
    if (!e) {
      this.handleTaskTemplateSearch();
      const stamp = taskCreatedByStamp();
      this.setState({
        notes: stamp,
        priority: undefined,
        taskDetails: undefined,
        task: undefined,
        assignTo: undefined,
      }, () => {
        this.formRef.current.setFieldsValue({
          notes: stamp,
          assignTo: undefined,
          priority: undefined,
          taskDetails: undefined,
          task: undefined,
        });
      });
      return;
    }
    const index = this.state.emaTaskTemplates.map(template => template.id).indexOf(e);
    const selectedTemplate = this.state.emaTaskTemplates[index];
    const assigtoArray = [...(selectedTemplate.assign_to_as_array || [])];
    if (assigtoArray.includes('Ordering Provider')) {
      assigtoArray.splice(assigtoArray.indexOf('Ordering Provider'), 1);
    }
    const newNotes = buildTaskTemplateNotes(selectedTemplate.notes);
    this.setState({
      priority: selectedTemplate.priority,
      taskDetails: selectedTemplate.task_details,
      task: selectedTemplate.task_type,
      assignTo: this.isKareoEmr() ? assigtoArray[0] : assigtoArray,
      notes: newNotes,
    }, () => {
      // Get form instance from ref
      this.formRef.current.setFieldsValue({
        priority: selectedTemplate.priority,
        taskDetails: selectedTemplate.task_details,
        task: selectedTemplate.task_type,
        assignTo: this.isKareoEmr() ? assigtoArray[0] : assigtoArray,
        notes: newNotes,
      });
    });
  };

  handleTaskTemplateSearch = (e) => {
    const unfilteredObjects = this.state.emaTaskTemplates;
    if (e) {
      const filtered = this.state.emaTaskTemplates.map(template => template.task_name).filter(option => (option.toLowerCase().includes(e.toLowerCase())));
      const filteredObject = [];
      filtered.forEach((template) => {
        const index = this.state.emaTaskTemplates.map(t => t.task_name).indexOf(template);
        filteredObject.push(this.state.emaTaskTemplates[index]);
      });
      this.setState({ searchedTemplates: filteredObject });
    } else {
      this.setState({ searchedTemplates: unfilteredObjects });
    }
  };

  handleSubmit = async () => {
    const {
      hideModal, triggerAutoUpload, triggerAutoUploadAndTaskCreation, modalType: { data: { sendToken } },
    } = this.props;
    const autoUploadEmr = localStorage.m8_auto_upload_emr;
    const {
      fileName,
      receiverType,
      documentCategory,
      providerName,
      patientFirstName,
      patientMiddleName,
      patientLastName,
      patientDOB,
      patientMrn,
      assignTo,
      task,
      taskDetails,
      dueDate,
      priority,
      notes,
      createTaskAfterUpload,
      noTaskCreation,
      practiceName,
      documentType,
      careTrackerProvider,
      dateOfService,
      facilityName,
      marketingRep,
      icd10Codes,
      orderingProvider,
      addAuthorization,
      authorizationType,
      authorizationNumber,
      authorizationDescription,
      authorizationActiveStatus,
      authorizationUnlimitedVisits,
      authorizationNumberOfVisits,
      authorizationFromDate,
      authorizationToDate,
      authorizationNote,
      authorizationReferringProvider,
      archiveAfterUpload,
      canExtractAuthorization,
    } = this.state;

    // Only send authorization data if feature flag is enabled AND user checked the checkbox
    const shouldSendAuthorization = addAuthorization && canExtractAuthorization;
    const shouldSendDateOfService = (
      autoUploadEmr === caretrackerEmr && localStorage.m8_org === caretrackerAccessOrg
    ) || this.shouldShowPerformedDateField(documentCategory);

    let formattedDOB = '';
    let formattedDueDate = '';
    let formattedDateOfService = '';
    const finalizedMarketingRep = this.props.allMarketingRepresentatives?.includes(marketingRep) ? marketingRep : null;
    if (patientDOB !== '') {
      const splitDate = patientDOB.split('/');
      formattedDOB = `${splitDate[2]}-${splitDate[0].padStart(2, '0')}-${splitDate[1].padStart(2, '0')}`;
    }
    if (dueDate !== '') {
      const splitDate = dueDate.split('/');
      formattedDueDate = `${splitDate[2]}-${splitDate[0].padStart(2, '0')}-${splitDate[1].padStart(2, '0')}`;
    }
    if (dateOfService !== '') {
      const splitDate = dateOfService.split('/');
      formattedDateOfService = `${splitDate[2]}-${splitDate[0].padStart(2, '0')}-${splitDate[1].padStart(2, '0')}`;
    }

    let uploadResult;
    if (createTaskAfterUpload && !noTaskCreation) {
      uploadResult = await triggerAutoUploadAndTaskCreation({
        sendToken, receiverType, documentCategory, fileName, patientFirstName, patientLastName, patientMiddleName, patientDOB: formattedDOB, patientMrn, assignTo, task, taskDetails, dueDate: formattedDueDate, priority, notes, facilityName, orderingProvider,
        dateOfService: shouldSendDateOfService ? formattedDateOfService : '',
        fromReferralManagement: this.props.modalType?.data?.fromReferralManagement === true,
        authorizationType: shouldSendAuthorization ? authorizationType : '',
        authorizationNumber: shouldSendAuthorization ? authorizationNumber : '',
        authorizationDescription: shouldSendAuthorization ? authorizationDescription : '',
        authorizationActiveStatus: shouldSendAuthorization ? authorizationActiveStatus : false,
        authorizationUnlimitedVisits: shouldSendAuthorization ? authorizationUnlimitedVisits : false,
        authorizationNumberOfVisits: shouldSendAuthorization ? authorizationNumberOfVisits : '',
        authorizationFromDate: shouldSendAuthorization ? authorizationFromDate : '',
        authorizationToDate: shouldSendAuthorization ? authorizationToDate : '',
        authorizationNote: shouldSendAuthorization ? authorizationNote : '',
        authorizationReferringProvider: shouldSendAuthorization ? authorizationReferringProvider : '',
        archiveAfterUpload: false,
      });
    } else {
      const assignee = (autoUploadEmr === caretrackerEmr && localStorage.m8_org === caretrackerAccessOrg) ? careTrackerProvider : assignTo || providerName;
      uploadResult = await triggerAutoUpload({
        sendToken,
        receiverType,
        documentCategory,
        fileName,
        practitionerName: assignee,
        patientFirstName,
        patientLastName,
        patientMiddleName,
        patientDOB: formattedDOB,
        patientMrn,
        practiceName,
        documentType,
        dateOfService: shouldSendDateOfService ? formattedDateOfService : '',
        facilityName,
        marketingRep: finalizedMarketingRep,
        icd10Codes,
        orderingProvider,
        fromReferralManagement: this.props.modalType?.data?.fromReferralManagement === true,
        emrPatientId: this.state.selectedEmrPatientId,
        authorizationType: shouldSendAuthorization ? authorizationType : '',
        authorizationNumber: shouldSendAuthorization ? authorizationNumber : '',
        authorizationDescription: shouldSendAuthorization ? authorizationDescription : '',
        authorizationActiveStatus: shouldSendAuthorization ? authorizationActiveStatus : false,
        authorizationUnlimitedVisits: shouldSendAuthorization ? authorizationUnlimitedVisits : false,
        authorizationNumberOfVisits: shouldSendAuthorization ? authorizationNumberOfVisits : '',
        authorizationFromDate: shouldSendAuthorization ? authorizationFromDate : '',
        authorizationToDate: shouldSendAuthorization ? authorizationToDate : '',
        authorizationNote: shouldSendAuthorization ? authorizationNote : '',
        authorizationReferringProvider: shouldSendAuthorization ? authorizationReferringProvider : '',
        archiveAfterUpload,
      });
    }
    if (uploadResult?.canceled) return;
    const { error, errorMessage } = this.props;
    if (!uploadResult?.error && !error) {
      hideModal();
      // ENG-3012 TIFF deferral: partial success (visible TIFFs excluded from a
      // reply filing) shows an acknowledged warning instead of the ordinary
      // success toast.
      const tiffWarnings = extractReplyTiffWarnings(uploadResult);
      if (tiffWarnings.length > 0) {
        showReplyTiffWarningModal({
          warnings: tiffWarnings,
          workflow: (createTaskAfterUpload && !noTaskCreation) ? 'legacy_upload_and_task' : 'legacy_send_to_chart',
        });
      } else {
        message.success({
          content: uploadResult?.repeatUploadAction === 'task_only'
            ? 'Task queued without uploading another document.'
            : (archiveAfterUpload
              ? 'Upload queued. The record will archive after successful filing if its status still allows it.'
              : 'Record will be uploaded shortly. We will notify you of any issues via email.'),
          duration: 1.5,
        });
      }
    } else {
      message.error({
        content: uploadResult?.response?.data?.error || errorMessage || 'Unable to queue the upload. Please try again.',
      });
    }
  };

  getOrderingProviderNames() {
    const src = this.props.allEmaTaskProviders;
    if (!Array.isArray(src) || src.length === 0) return [];
    return src
      .filter((p) => p && p.is_provider)
      .map((p) => p.name)
      .filter(Boolean);
  }

  isKareoEmr() {
    return localStorage.getItem('m8_auto_upload_emr') === 'Kareo';
  }

  parseName(name) {
    let firstName = '';
    let lastName = '';
    const parts = name.split(',');
    lastName = parts[0].trim();
    firstName = parts[1].trim();

    // Handle edge cases for Spanish names with multiple surnames
    if (lastName.split(' ').length > 1 && !lastName.includes('-')) {
      const lastNames = lastName.split(' ');
      lastName = `${lastNames[0]} ${lastNames[1]}`;
    }

    return { firstName, lastName };
  }

  renderAssignToSelect() {
    const autoUploadEmr = localStorage.m8_auto_upload_emr;
    const noProviderMessage = 'Contact your Admin to add EMR Providers';
    const providers = autoUploadEmr === 'Kareo'
      ? this.props.allKareoTaskProviders
      : this.props.allEmaTaskProviders;
    const noProvidersAvailable = !providers || providers.length === 0;
    return (
      <Form.Item
        name="assignTo"
        label={noProvidersAvailable
          ? (
            <div>
              Assign to EMR Staff or Staff Group
              <Tooltip title="Please contact your Admin to add Providers">
                <InfoCircleOutlined style={{ paddingLeft: '2px', marginTop: '6px', height: '8px' }} />
              </Tooltip>
            </div>
          ) : (
            <div>
              Assign to EMR Staff or Staff Group
            </div>
          )}
        style={{ flex: 1 }}
      >
        <Select
          mode={autoUploadEmr === 'EMA' ? 'multiple' : undefined}
          showSearch
          placeholder="Select a provider"
          optionFilterProp="children"
          onChange={e => this.handleSelectionChange(e, 'assignTo')}
          onSearch={this.handleAssignToSearch}
          filterOption={false}
          value={this.state.assignTo}
          allowClear
          notFoundContent={noProviderMessage}
        >
          {this.state.searchedNames && this.state.searchedNames.map((e, index) => (
            <Select.Option key={e + index} value={e}>
              {e}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    );
  }

  renderDocumentTypeSelect() {
    return (
      <Form.Item
        name="documentType"
        label="Document Type"
        style={{ flex: 1 }}
      >
        <Select placeholder="Select Document Type" onChange={e => this.handleSelectionChange(e, 'documentType')}>
          <Select.Option value="Clinical">Clinical</Select.Option>
          <Select.Option value="Patient">Patient</Select.Option>
        </Select>
      </Form.Item>
    );
  }

  renderCareTrackerDocumentTitleAndDateOfService() {
    return (
      <div style={{ display: 'flex', gap: '10px' }}>
        <Form.Item
          hasFeedback
          label="Document Title"
          name="fileName"
          style={{ flex: 1 }} // Added flex to ensure it takes available space
        >
          <Input
            placeholder="Document Title"
            name="fileName"
            onChange={this.handleFieldChange}
          />
        </Form.Item>

        <Form.Item
          label="Date of Service"
          style={{ flex: 0.3 }}
        >
          <DatePicker
            onChange={this.handleDateOfServiceChange}
            name="dateOfService"
            format={['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']}
            value={this.state.dateOfService && moment(this.state.dateOfService, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'])}
          />
        </Form.Item>
      </div>
    );
  }

  renderCareTrackerProviderSelect() {
    return (
      <Form.Item
        name="Provider"
        label="Provider"
        style={{ flex: 1 }}
      >
        <Select
          placeholder="Select Provider"
          showSearch
          optionFilterProp="children"
          allowClear
          onChange={e => this.handleSelectionChange(e, 'careTrackerProvider')}
        >
          {caretrackerProviders.map(provider => (
            <Select.Option key={provider} value={provider}>
              {provider}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    );
  }

  renderPatientName() {
    return (
      <div style={{ display: 'flex', gap: '10px' }}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Form.Item
            label="Patient First Name"
            style={{ flex: 1 }}
          >
            <Input
              placeholder="First name"
              name="patientFirstName"
              value={this.state.patientFirstName}
              onChange={this.handleFieldChange}
            />
          </Form.Item>
          <Form.Item
            label="Patient Middle Name"
            style={{ flex: 1 }}
          >
            <Input
              placeholder="Middle name"
              name="patientMiddleName"
              value={this.state.patientMiddleName}
              onChange={this.handleFieldChange}
            />
          </Form.Item>
          <Form.Item
            label="Patient Last Name"
            style={{ flex: 1 }}
          >
            <Input
              placeholder="Last name"
              name="patientLastName"
              value={this.state.patientLastName}
              onChange={this.handleFieldChange}
            />
          </Form.Item>
        </div>
      </div>
    );
  }

  renderDocumentCategorySelect(documentCategories, documentCategory) {
    const autoUploadEmr = localStorage.m8_auto_upload_emr;
    const categorySelect = (
      <Form.Item
        name="documentCategory"
        label="Document Category"
        style={{ flex: 1 }}
        rules={autoUploadEmr === 'EMA' ? [
          () => ({
            validator(_, name) {
              if (name && name.trim() !== '') return Promise.resolve();
              return Promise.reject('Category cannot be blank');
            },
          }),
        ] : []}
      >
        <Select
          label="Document Category"
          placeholder="Select a document category"
          allowClear
          showSearch
          onChange={this.handleDocumentCategoryChange}
          value={documentCategory}
        >
          {dedupeDocumentCategories(documentCategories).map((emrCategory) => (
            <Select.Option key={emrCategory.id} value={emrCategory.emr_category}>
              {emrCategory.emr_category}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    );

    const showOrderingProviderField = this.shouldShowOrderingProviderField(documentCategory);
    const showPerformedDateField = this.shouldShowPerformedDateField(documentCategory);
    const isEMA = autoUploadEmr === 'EMA';
    if ((!showOrderingProviderField && !showPerformedDateField) || !isEMA) return categorySelect;

    return (
      <div style={{ display: 'flex', gap: '10px' }}>
        {categorySelect}
        {showOrderingProviderField && (
          <Form.Item
            name="orderingProvider"
            label={(
              <span>
                {this.isScopedManualResultsFlow(documentCategory) ? 'Provider' : 'Ordering Provider'}
                {localStorage.m8_auto_upload_emr === 'EMA' && (
                  <Tooltip title="Start typing to search and select a matching provider from your EMR. If our AI extracted the provider, we will try to preselect it for you. You can also leave this field blank.">
                    <InfoCircleOutlined style={{ color: '#1890ff', fontSize: 16, marginLeft: 4 }} />
                  </Tooltip>
                )}
              </span>
            )}
            style={{ flex: 1 }}
          >
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Select provider"
              onChange={(e) => this.handleSelectionChange(e, 'orderingProvider')}
              value={this.state.orderingProvider}
              allowClear
            >
              {this.getOrderingProviderNames().map((name) => (
                <Select.Option key={name} value={name}>{name}</Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}
        {showPerformedDateField && (
          <Form.Item
            label="Performed Date"
            style={{ flex: 0.7 }}
          >
            <DatePicker
              onChange={this.handleDateOfServiceChange}
              name="dateOfService"
              format={['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']}
              value={this.state.dateOfService && moment(this.state.dateOfService, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'])}
            />
          </Form.Item>
        )}
      </div>
    );
  }

  handleSearchTypeChange = (value) => {
    this.setState({ 
      searchType: value, 
      searchedPatientDOB: '', 
      searchedPatientMRN: '', 
      searchedPatientFirstName: '',
      searchedPatientMiddleName: '',
      searchedPatientLastName: ''
    });
  };

  getSearchConfig() {
    const { searchType, searchedPatientMRN, searchedPatientFirstName, searchedPatientLastName, searchedPatientDOB } = this.state;
    const dateFormat = ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'];
    const searchedPatientDOBMoment = searchedPatientDOB ? moment(searchedPatientDOB, dateFormat, true) : null;
    const searchedPatientDOBValue = searchedPatientDOBMoment && searchedPatientDOBMoment.isValid() ? searchedPatientDOBMoment : null;

    const searchConfigs = {
      mrn: {
        input: (
          <Input
            key={`mrn-input-${searchType}`}
            name="searchedPatientMRN"
            placeholder="Patient MRN"
            onChange={e => this.setState({ searchedPatientMRN: e.target.value })}
            style={{ width: '200px' }}
            allowClear
          />
        ),
        button: {
          onClick: () => this.handleSearchPatient('mrn'),
          disabled: !searchedPatientMRN,
          text: 'Search',
        }
      },
      name: {
        input: (
          <div key={`name-input-${searchType}`} style={{ display: 'flex', gap: '8px' }}>
            <Input
              placeholder="First"
              onChange={e => this.setState({ searchedPatientFirstName: e.target.value })}
              style={{ width: '120px' }}
              allowClear
            />
            <Input
              placeholder="Middle"
              onChange={e => this.setState({ searchedPatientMiddleName: e.target.value })}
              style={{ width: '90px' }}
              allowClear
            />
            <Input
              placeholder="Last"
              onChange={e => this.setState({ searchedPatientLastName: e.target.value })}
              style={{ width: '120px' }}
              allowClear
            />
          </div>
        ),
        button: {
          onClick: () => this.handleSearchPatient('name'),
          disabled: !searchedPatientFirstName && !searchedPatientLastName,
          text: 'Search',
        }
      },
      dob: {
        input: (
          <DatePicker
            key={`dob-input-${searchType}`}
            placeholder="Date of Birth"
            onChange={(_, dateString) => this.setState({ searchedPatientDOB: dateString })}
            format={dateFormat}
            value={searchedPatientDOBValue}
            style={{ width: '180px' }}
          />
        ),
        button: {
          onClick: () => this.handleSearchPatient('dob'),
          disabled: !searchedPatientDOB,
          text: 'Search',
        }
      },
      nameAndDOB: {
        input: (
          <div key={`name-and-dob-input-${searchType}`} onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Input
                placeholder="First"
                onChange={e => this.setState({ searchedPatientFirstName: e.target.value })}
                style={{ width: '120px' }}
                allowClear
              />
              <Input
                placeholder="Middle"
                onChange={e => this.setState({ searchedPatientMiddleName: e.target.value })}
                style={{ width: '90px' }}
                allowClear
              />
              <Input
                placeholder="Last"
                onChange={e => this.setState({ searchedPatientLastName: e.target.value })}
                style={{ width: '120px' }}
                allowClear
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <DatePicker
                onChange={(_, dateString) => this.setState({ searchedPatientDOB: dateString })}
                format={dateFormat}
                value={searchedPatientDOBValue}
                placeholder="Date of Birth"
                style={{ width: '160px' }}
              />
            </div>
          </div>
        ),
        button: {
          onClick: () => this.handleSearchPatient('nameAndDOB'),
          disabled: !((searchedPatientFirstName || searchedPatientLastName) && searchedPatientDOB),
          text: 'Search',
        }
      },
    };

    return searchConfigs[searchType];
  };

  renderSearchInput() {
    const config = this.getSearchConfig();
    return config.input;
  };

  renderSearchButton() {
    const config = this.getSearchConfig();
    return (
      <Button
        icon={<SearchOutlined />}
        type="dashed"
        onClick={config.button.onClick}
        disabled={config.button.disabled}
      >
        {config.button.text}
      </Button>
    );
  };

  getTableColumns() {
    const autoUploadEmr = localStorage.getItem('m8_auto_upload_emr');
    // Tebra doesn't use MRN, only EMA does
    return autoUploadEmr === 'Kareo'
      ? resultsTable.filter(col => col.key !== 'mrn')
      : resultsTable;
  };

  renderLoadingIndicator() {
    const { loading } = this.state;
    if (!loading) return null;

    const autoUploadEmr = localStorage.getItem('m8_auto_upload_emr');
    const emrName = autoUploadEmr === 'Kareo' ? 'Tebra' : 'ModMed';

    return (
      <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Contacting {emrName}</div>
          <Progress percent={100} status="active" showInfo={false} />
      </div>
    );
  };

  renderSearchResults() {
    const { apiPatientsList } = this.state;
    if (apiPatientsList.length === 0) return null;

    return (
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
        <Table
          title={() => "Search Results:"}
          pagination={{ position: ["none"], pageSize: 20 }}
          scroll={{ y: 100 }}
          columns={this.getTableColumns()}
          dataSource={apiPatientsList.map(record => ({ ...record, key: record.mrn || record.id }))}
          onRow={(record) => ({
            onClick: () => this.handlePatientSelection(record),
          })}
        />
      </div>
    );
  };

  handlePatientSelection = (record) => {
    // Validate required fields
    if (!record || !record.name || !record.dob) {
      return;
    }

    const isKareo = this.isKareoEmr();

    const fullName = record.name;
    const dob = record.dob;
    const mrn = record.mrn;
    const id = record.id;
    const patientName = fullName.trim();
    const patientMrn = mrn ? mrn.trim() : '';
    const formattedDOB = moment(dob.trim()).format('MM/DD/YYYY');

    // For Tebra: use patient ID, for EMA: use MRN
    const selectedEmrPatientId = isKareo ? id : mrn;

    const parsedName = parseFullName(patientName);

    this.setState({
      patientFirstName: parsedName.first || '',
      patientMiddleName: parsedName.middle || '',
      patientLastName: parsedName.last || '',
      patientMrn,
      patientDOB: formattedDOB,
      open: false,
      showNameMismatchWarning: false,
      selectedEmrPatientId,
    }, () => {
      // Sync form fields with updated state
      if (this.formRef.current) {
        this.formRef.current.setFieldsValue({
          patientDOB: moment(formattedDOB, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']),
        });
      }
    });
  };

  handleSearchPatient = async (type) => {
    const { getEmaPatients, getTebraPatients } = this.props;
    const { searchedPatientDOB, searchedPatientMRN, searchedPatientFirstName, searchedPatientMiddleName, searchedPatientLastName } = this.state;
    const isKareo = this.isKareoEmr();

    let searchQuery = '';
    if (type === 'mrn') {
      searchQuery = `?mrn=${encodeURIComponent(searchedPatientMRN)}`;
    } else if (type === 'name') {
      const params = [];
      if (searchedPatientFirstName) {
        params.push(`first_name=${encodeURIComponent(searchedPatientFirstName.trim())}`);
      }
      if (searchedPatientLastName) {
        params.push(`last_name=${encodeURIComponent(searchedPatientLastName.trim())}`);
      }
      if (searchedPatientMiddleName) {
        params.push(`middle_name=${encodeURIComponent(searchedPatientMiddleName.trim())}`);
      }
      searchQuery = `?${params.join('&')}`;
    } else if (type === 'dob') {
      searchQuery = `?dob=${encodeURIComponent(searchedPatientDOB.trim())}`;
    } else {
      const params = [];
      if (searchedPatientFirstName) {
        params.push(`first_name=${encodeURIComponent(searchedPatientFirstName.trim())}`);
      }
      if (searchedPatientLastName) {
        params.push(`last_name=${encodeURIComponent(searchedPatientLastName.trim())}`);
      }
      if (searchedPatientMiddleName) {
        params.push(`middle_name=${encodeURIComponent(searchedPatientMiddleName.trim())}`);
      }
      if (searchedPatientDOB) {
        params.push(`dob=${encodeURIComponent(searchedPatientDOB.trim())}`);
      }
      searchQuery = `?${params.join('&')}`;
    }

    this.setState({ loading: true });

    // Call the appropriate API based on EMR type
    if (isKareo) {
      await getTebraPatients(searchQuery);
    } else {
      await getEmaPatients(searchQuery);
    }

    if (this.props.apiError) {
      this.setState({
        loading: false,
      });
      toastr.error(this.props.apiErrorMessage);
      return;
    }
    this.setState({
      apiPatientsList: this.props.apiPatients.patients,
      open: true,
      loading: false,
    });
    if (this.state.apiPatientsList.length === 0) {
      const emrName = isKareo ? 'Tebra' : 'EMA';
      toastr.warning(`No patients found in ${emrName} with the following information`);
    }
    if (this.autoCompleteRef.current) {
      this.autoCompleteRef.current.focus();
    }
  }

  renderApiSearchBar() {
    const isKareo = this.isKareoEmr();

    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          <Typography.Text style={{ marginRight: '10px' }}>Search Patient By:</Typography.Text>
          <Select
            defaultValue="name"
            onChange={this.handleSearchTypeChange}
            style={{ width: '130px' }}
          >
            <Select.Option value="name">Name</Select.Option>
            <Select.Option value="dob">DOB</Select.Option>
            {!isKareo && <Select.Option value="mrn">MRN</Select.Option>}
            <Select.Option value="nameAndDOB">Name & DOB</Select.Option>
          </Select>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {this.renderSearchInput()}
          </div>
          {this.renderSearchButton()}
        </div>
        {this.renderLoadingIndicator()}
        {this.renderSearchResults()}
      </>
    );
  }

  renderPatientMRNandDOB(emr, patientMrn, patientDOB) {
    const dateFormat = ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'];
    const isKareo = this.isKareoEmr();
    return (
      <div style={{ display: 'flex' }}>
        <Form.Item
          label="Patient Date of Birth"
          style={{ width: '175px' }}
        >
          <DatePicker
            onChange={this.handleDateChange}
            name="patientDOB"
            format={dateFormat}
            defaultValue={patientDOB && moment(patientDOB, dateFormat)}
            value={patientDOB && moment(patientDOB, dateFormat)}
          />
        </Form.Item>
        {!isKareo && <Form.Item
          label={emr === 'ECW' ? 'Account Number (Optional)' : 'Patient MRN (Optional)'}
          style={{ width: '175px' }}
        >
          <Input
            placeholder={emr === 'ECW' ? 'Account Number' : 'Patient MRN'}
            name="patientMrn"
            defaultValue={patientMrn}
            value={patientMrn}
            onChange={this.handleFieldChange}
          />
        </Form.Item>}
      </div>
    );
  }

  handleICD10CodesChange = (value) => {
    this.setState({ icd10Codes: value });
  };

  handleMarketingRepChange = (value) => {
    this.setState({ marketingRep: value });
  };

  render() {
    const { hideModal } = this.props;
    const
      {
        fileName,
        documentCategories,
        documentCategory,
        providerName,
        patientFirstName,
        patientLastName,
        patientDOB,
        patientMrn,
        notes,
        assignTo,
        createTaskAfterUpload,
        noTaskCreation,
        practiceName,
        documentType,
        careTrackerProvider,
        apiCredentials,
        receiverType,
      } = this.state;
    const autoUploadEmr = localStorage.m8_auto_upload_emr;
    const docTitleSupport = ['Kareo', 'OncoEMR', 'Nymbl', 'EMA', 'ECW', 'AllegianceMD', 'Elation'];
    const customContentStyle = {
      width: '340px',
    };
    const canCreateTaskAfterUpload = autoUploadEmr === 'EMA' || autoUploadEmr === 'Kareo';
    const supportsArchiveCompletion = autoUploadEmr !== 'Welkin' && !(autoUploadEmr === 'Kareo' && apiCredentials);
    const taskCheckboxLabel = autoUploadEmr === 'Kareo' ? 'Create note after upload' : 'Create task after upload';
    const disableSubmitButton = () => {
      // Authorization validations (only when addAuthorization is checked and feature flag enabled) - EMA only
      const authorizationValidationsFailed = autoUploadEmr === 'EMA' && this.state.canExtractAuthorization && this.state.addAuthorization && (
        !this.state.authorizationType
        || !this.state.authorizationNumber
        || !this.state.authorizationDescription
        || (this.state.authorizationUnlimitedVisits
          ? (!this.state.authorizationFromDate || !this.state.authorizationToDate)
          : !this.state.authorizationNumberOfVisits
        )
      );

      if (this.state.createTaskAfterUpload && !noTaskCreation) {
        const isKareo = autoUploadEmr === 'Kareo';

        // Base requirements for all EMRs: need (first AND last name AND DOB) OR MRN
        const missingPatientInfo = (((patientFirstName === '' || patientLastName === '') || patientDOB === '') && patientMrn === '');
        const missingAssignee = (this.state.assignTo && this.state.assignTo.length === 0) || !this.state.assignTo;
        const missingCategory = autoUploadEmr === 'EMA' && !documentCategory;

        if (isKareo) {
          return missingPatientInfo || missingAssignee || missingCategory;
        }

        // Falsy check (not `=== ''`): clearing the Task Template sets these
        // to undefined, which would slip past a strict empty-string compare.
        return (
          missingPatientInfo
          || missingAssignee
          || !this.state.task
          || !this.state.taskDetails
          || missingCategory
          || authorizationValidationsFailed
        );
      }
      if (autoUploadEmr === 'EMA') {
        return (
          (docTitleSupport.includes(autoUploadEmr) ? fileName === '' : providerName === '')
          || !documentCategory
          || (((patientFirstName === '' || patientLastName === '') || patientDOB === '') && patientMrn === '')
          || authorizationValidationsFailed
        );
      }
      if (autoUploadEmr === 'ECW') {
        return (
          (((patientFirstName === '' || patientLastName === '') && patientDOB === '') && patientMrn === '')
          || !assignTo
          || !documentCategory
        );
      }
      if (autoUploadEmr === 'AdvancedMD') {
        return providerName === '' || providerName === null;
      }
      if (autoUploadEmr === caretrackerEmr && localStorage.m8_org === caretrackerAccessOrg) {
        return !careTrackerProvider || documentType === '' || !documentCategory;
      }
      return (
        (docTitleSupport.includes(autoUploadEmr) ? fileName === '' : providerName === '')
        || (((patientFirstName === '' || patientLastName === '') || patientDOB === '') && patientMrn === '')
      );
    };

    return (
      <div>
        <ModalWrapper
          customContentStyle={customContentStyle}
          width={522}
          hideModal={hideModal}
          dismiss="Cancel"
          action={this.handleSubmit}
          actionName="Submit"
          disabled={disableSubmitButton()}
          modalTitle="Confirm document"
          isDraggable
        >
          {apiCredentials && (autoUploadEmr === 'EMA' || autoUploadEmr === 'Kareo') && this.renderApiSearchBar()}
          <Form
            ref={this.formRef}
            layout="vertical"
            onFinish={this.handleSubmit}
            initialValues={{
              taskAfterUpload: createTaskAfterUpload, documentCategory, providerName, fileName, notes,
            }}
          >
            {this.state.showNameMismatchWarning && (
              <Alert
                message="Patient Name Mismatch Detected"
                description={(
                  <div>
                    Please verify the patient's name before sending to chart. Due to a recent
                    system migration, some previously edited values may be inconsistent.
                    <br />
                    <br />
                    Current name values:
                    <br />
                    Extracted name: <span style={{ color: '#722ed1' }}>{this.state.initialParsedName}</span>
                    <br />
                    Updated name: <span style={{ color: '#722ed1' }}>{this.props.modalType.data.patientName}</span>
                  </div>
                )}
                type="warning"
                showIcon
                style={{ marginBottom: '16px' }}
              />
            )}
            {(docTitleSupport.includes(autoUploadEmr))
              && (
                <Form.Item
                  hasFeedback
                  label="Document Title Name"
                  name="fileName"
                  rules={[
                    () => ({
                      validator(_, name) {
                        if (name && name.trim() !== '') return Promise.resolve();
                        return Promise.reject('Name cannot be blank');
                      },
                    }),
                  ]}
                >
                  <Input
                    placeholder="Document Title Name"
                    name="fileName"
                    onChange={this.handleFieldChange}
                  />
                </Form.Item>
              )}
            {autoUploadEmr === 'ECW'
              && (
                <>
                  {this.renderPatientName()}
                  <Form.Item
                    label="Patient Date of Birth"
                    style={{ width: '175px' }}
                  >
                    <DatePicker
                      onChange={this.handleDateChange}
                      name="patientDOB"
                      format={['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']}
                      defaultValue={patientDOB && moment(patientDOB, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'])}
                      value={patientDOB && moment(patientDOB, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'])}
                    />
                  </Form.Item>
                  {this.renderPatientMRNandDOB(autoUploadEmr, patientMrn, patientDOB)}
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <div style={{ flex: 0.5 }}>
                      {this.renderDocumentCategorySelect(documentCategories, documentCategory)}
                    </div>
                    <div style={{ flex: 0.5 }}>
                      {this.renderAssignToSelect(true)}
                    </div>
                  </div>
                </>
              )}
            {categoryRequiredEmrs.includes(autoUploadEmr)
              && this.renderDocumentCategorySelect(documentCategories, documentCategory)}
            {autoUploadEmr === 'AllegianceMD' && this.renderAssignToSelect(true)}
            {autoUploadEmr === caretrackerEmr && localStorage.m8_org === caretrackerAccessOrg && (
              <>
                {this.renderCareTrackerDocumentTitleAndDateOfService()}
                {this.renderDocumentTypeSelect()}
                {this.renderCareTrackerProviderSelect()}
              </>
            )}
            {autoUploadEmr === 'Brightree'
              && (
                <>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '16px' }}>
                    <Form.Item
                      label="Patient First Name"
                      style={{ width: '150px' }}
                    >
                      <Input
                        placeholder="First name"
                        name="patientFirstName"
                        value={this.state.patientFirstName}
                        onChange={this.handleFieldChange}
                      />
                    </Form.Item>
                    <Form.Item
                      label="Patient Middle Name"
                      style={{ width: '150px' }}
                    >
                      <Input
                        placeholder="Middle name"
                        name="patientMiddleName"
                        value={this.state.patientMiddleName}
                        onChange={this.handleFieldChange}
                      />
                    </Form.Item>
                    <Form.Item
                      label="Patient Last Name"
                      style={{ width: '150px' }}
                    >
                      <Input
                        placeholder="Last name"
                        name="patientLastName"
                        value={this.state.patientLastName}
                        onChange={this.handleFieldChange}
                      />
                    </Form.Item>
                    <Form.Item
                      label="Patient Date of Birth"
                      style={{ width: '175px' }}
                    >
                      <DatePicker
                        onChange={this.handleDateChange}
                        name="patientDOB"
                        format={['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']}
                        defaultValue={patientDOB && moment(patientDOB, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'])}
                        value={patientDOB && moment(patientDOB, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'])}
                      />
                    </Form.Item>
                  </div>
                  <Form.Item
                    label="ICD-10 Codes"
                    style={{ width: '450px' }}
                  >
                    <Select
                      mode="tags"
                      name="icd10Codes"
                      placeholder="Enter ICD-10 codes (separated by commas)"
                      style={{ width: '100%' }}
                      onChange={this.handleICD10CodesChange}
                      tokenSeparators={[',']}
                      notFoundContent={null}
                      value={this.state.icd10Codes}
                      defaultValue={this.state.icd10Codes}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Marketing Representative"
                    style={{ width: '300px' }}
                  >
                    <Select
                      placeholder="Select a marketing representative"
                      style={{ width: '100%' }}
                      onChange={this.handleMarketingRepChange}
                      value={this.props.allMarketingRepresentatives?.includes(this.state.marketingRep) ? this.state.marketingRep : null}
                      defaultValue={this.props.allMarketingRepresentatives?.includes(this.state.marketingRep) ? this.state.marketingRep : null}
                      allowClear
                      notFoundContent={
                        (!this.props.allMarketingRepresentatives || this.props.allMarketingRepresentatives.length === 0)
                          ? 'No marketing representatives found. Please contact your admin to add them.'
                          : null
                      }
                    >
                      {this.props.allMarketingRepresentatives && this.props.allMarketingRepresentatives.map((name) => (
                        <Select.Option key={name} value={name}>
                          {name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </>
              )}
            {(autoUploadEmr === 'AdvancedMD')
              && (
                <>
                  {this.renderDocumentCategorySelect(documentCategories, documentCategory)}
                  <Form.Item
                    hasFeedback
                    label="Provider Name"
                    name="providerName"
                    rules={[
                      () => ({
                        validator(_, name) {
                          if (name && name.trim() !== '') return Promise.resolve();
                          return Promise.reject('Name cannot be blank');
                        },
                      }),
                    ]}
                    style={{ flex: 1 }}
                  >
                    <Input
                      placeholder="Last name, First name"
                      name="providerName"
                      onChange={this.handleFieldChange}
                    />
                  </Form.Item>
                </>
              )}

            {(autoUploadEmr === 'EMA' || autoUploadEmr === 'Kareo')
              && (
                <>
                  {this.renderPatientName()}
                  {this.renderPatientMRNandDOB(autoUploadEmr, patientMrn, patientDOB)}
                </>
              )}

            {canCreateTaskAfterUpload && !noTaskCreation
              && (
                <Form.Item
                  style={{ display: 'flex', marginTop: 20 }}
                  name="taskAfterUpload"
                  valuePropName="checked"
                  onChange={this.handleCreateTaskAfterUpload}
                >
                  <Checkbox>{taskCheckboxLabel}</Checkbox>
                </Form.Item>
              )}

            {(localStorage.m8_org === 'PrePost+' && practiceName)
              && (
                <Form
                  initialValues={{
                    locationSelection: practiceName,
                  }}
                >
                  <Form.Item
                    style={{ display: 'flex', marginTop: 20 }}
                    name="locationSelection"
                    label="Location"
                  >
                    <Select
                      onChange={value => this.setState({ practiceName: value })}
                      value={practiceName}
                      options={PREPOST_PRACTICE_OPTIONS}
                    >
                    </Select>
                  </Form.Item>
                </Form>
               )
            }

            {(createTaskAfterUpload && !noTaskCreation)
              && (
                <>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {['EMA', 'Kareo'].includes(autoUploadEmr)
                      && (
                        <Form.Item
                          label={autoUploadEmr === 'Kareo' ? 'Note Templates' : 'Task Templates'}
                          name="taskTemplates"
                          style={{ flex: 0.5 }}
                        >
                          <Select
                            showSearch
                            optionFilterProp="children"
                            onChange={this.handleTaskTemplateChange}
                            onSearch={this.handleTaskTemplateSearch}
                            filterOption={false}
                            allowClear
                          >
                            {this.state.searchedTemplates && this.state.searchedTemplates.map(e => (
                              <Select.Option key={e.id} value={e.id}>
                                {e.task_name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      )}

                    {localStorage.m8_auto_upload_emr === 'EMA' && this.state.apiCredentials
                      && (
                        <Form.Item
                          label="Facility (Optional)"
                          style={{ flex: 0.4 }}
                        >
                          <Select
                            showSearch
                            placeholder="Facility"
                            value={this.state.facilityName}
                            optionFilterProp="children"
                            onChange={this.handleFacilityChange}
                            onSearch={this.handleFacilitySearch}
                            filterOption={false}
                            allowClear
                          >
                            {this.state.searchedFacilities.map(facility => (
                              <Select.Option key={facility.id} value={facility.name}>
                                {facility.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      )}

                  </div>
                  {autoUploadEmr !== 'Kareo' && (
                    <>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <Form.Item
                          label="Task"
                          style={{ flex: 0.35 }}
                        >
                          <Select
                            showSearch
                            placeholder="Task"
                            value={this.state.task}
                            optionFilterProp="children"
                            onChange={e => this.handleSelectionChange(e, 'task')}
                            onSearch={this.handleTaskSearch}
                            filterOption={false}
                          >
                            {this.state.searchedTasks.map(e => (
                              <Select.Option key={e} value={e}>
                                {e}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <Form.Item
                          label="Task Details"
                          style={{ flex: 0.35 }}
                        >
                          <AutoComplete
                            placeholder="Task Details"
                            value={this.state.taskDetails}
                            options={this.state.taskDetailsSearchOptions.map(option => ({ value: option }))}
                            onSearch={this.handleSearchTaskDetails}
                            onSelect={e => this.handleSelectionChange(e, 'taskDetails')}
                          />
                        </Form.Item>
                        <Form.Item
                          label="Due Date"
                          style={{ flex: 0.3 }}
                        >
                          <DatePicker
                            onChange={this.handleDueDateChange}
                            name="dueDate"
                            format={['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']}
                            value={this.state.dueDate && moment(this.state.dueDate, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'])}
                          />
                        </Form.Item>
                      </div>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        {this.renderAssignToSelect()}
                        <Form.Item
                          label="Priority"
                          style={{ flex: 0.5 }}
                        >
                          <Select
                            allowClear={false}
                            value={this.state.priority}
                            options={priorityOptions.map(option => ({ value: option }))}
                            onChange={e => this.handleSelectionChange(e, 'priority')}
                          />
                        </Form.Item>
                      </div>
                    </>
                  )}
                  {autoUploadEmr === 'Kareo' && (
                    <div style={{ display: 'flex', gap: '10px' }}>
                      {this.renderAssignToSelect()}
                    </div>
                  )}
                  <Form.Item
                    name="notes"
                    label="Notes (optional)"
                  >
                    <Input.TextArea
                      onChange={this.handleNotesChange}
                      placeholder="Notes (optional)"
                      name="notes"
                      value={this.state.notes}
                    />
                  </Form.Item>
                </>
              )}
            {/* Authorization Section - Only show for EMA users with feature flag enabled */}
            {autoUploadEmr === 'EMA' && this.state.canExtractAuthorization && (
              <>
                <Divider />
                <Form.Item>
                  <Checkbox
                    checked={this.state.addAuthorization}
                    onChange={(e) => {
                      this.setState({ addAuthorization: e.target.checked });
                      if (!e.target.checked) {
                        // Reset authorization fields when unchecked
                        this.setState({
                          authorizationType: '',
                          authorizationNumber: '',
                          authorizationDescription: '',
                          authorizationActiveStatus: true,
                          authorizationUnlimitedVisits: false,
                          authorizationNumberOfVisits: '',
                          authorizationFromDate: '',
                          authorizationToDate: '',
                          authorizationNote: '',
                          authorizationReferringProvider: '',
                        });
                      }
                    }}
                  >
                    Add Insurance Authorization
                  </Checkbox>
                </Form.Item>
                {this.state.addAuthorization && (
                  <>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <Form.Item
                        label={<span>Authorization Type <span style={{ color: 'red' }}>*</span></span>}
                        style={{ flex: 0.5 }}
                      >
                        <Select
                          placeholder="Select type"
                          value={this.state.authorizationType || undefined}
                          onChange={(value) => this.setState({ authorizationType: value })}
                        >
                          {authorizationTypeOptions.map((option) => (
                            <Select.Option key={option} value={option}>
                              {option}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        label={<span>Authorization Number <span style={{ color: 'red' }}>*</span></span>}
                        style={{ flex: 0.5 }}
                      >
                        <Input
                          placeholder="Authorization number"
                          value={this.state.authorizationNumber}
                          onChange={(e) => this.setState({ authorizationNumber: e.target.value })}
                        />
                      </Form.Item>
                    </div>
                    <Form.Item
                      label={<span>Description <span style={{ color: 'red' }}>*</span></span>}
                    >
                      <Input
                        placeholder="Authorization description"
                        value={this.state.authorizationDescription}
                        onChange={(e) => this.setState({ authorizationDescription: e.target.value })}
                      />
                    </Form.Item>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                      {!this.state.authorizationUnlimitedVisits && (
                        <Form.Item
                          label={<span>Number of Visits <span style={{ color: 'red' }}>*</span></span>}
                          style={{ flex: 0.3 }}
                        >
                          <Input
                            type="number"
                            placeholder="Visits"
                            value={this.state.authorizationNumberOfVisits}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || (parseInt(value, 10) >= 1 && parseInt(value, 10) <= 99)) {
                                this.setState({ authorizationNumberOfVisits: value });
                              }
                            }}
                            min={1}
                            max={99}
                          />
                        </Form.Item>
                      )}
                      <Form.Item
                        label="Status"
                        style={{ flex: 0.35 }}
                      >
                        <Select
                          value={this.state.authorizationActiveStatus ? 'Active' : 'Inactive'}
                          onChange={(value) => this.setState({ authorizationActiveStatus: value === 'Active' })}
                        >
                          <Select.Option value="Active">Active</Select.Option>
                          <Select.Option value="Inactive">Inactive</Select.Option>
                        </Select>
                      </Form.Item>
                      <Form.Item style={{ flex: 0.35, marginBottom: '24px' }}>
                        <Checkbox
                          checked={this.state.authorizationUnlimitedVisits}
                          onChange={(e) => {
                            this.setState({
                              authorizationUnlimitedVisits: e.target.checked,
                              // Clear number of visits when unlimited is checked
                              authorizationNumberOfVisits: e.target.checked ? '' : this.state.authorizationNumberOfVisits,
                            });
                          }}
                        >
                          Unlimited Visits
                        </Checkbox>
                      </Form.Item>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <Form.Item
                        label={<span>From Date {this.state.authorizationUnlimitedVisits && <span style={{ color: 'red' }}>*</span>}</span>}
                        style={{ flex: 0.5 }}
                      >
                        <DatePicker
                          format={['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']}
                          value={this.state.authorizationFromDate ? moment(this.state.authorizationFromDate, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']) : null}
                          onChange={(_, dateString) => this.setState({ authorizationFromDate: dateString })}
                          placeholder="From date"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                      <Form.Item
                        label={<span>To Date {this.state.authorizationUnlimitedVisits && <span style={{ color: 'red' }}>*</span>}</span>}
                        style={{ flex: 0.5 }}
                      >
                        <DatePicker
                          format={['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']}
                          value={this.state.authorizationToDate ? moment(this.state.authorizationToDate, ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY']) : null}
                          onChange={(_, dateString) => this.setState({ authorizationToDate: dateString })}
                          placeholder="To date"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </div>
                    <Form.Item
                      label={
                        (
                          <span>
                            Referring Provider (optional)
                            {this.state.apiCredentials && (
                              <Tooltip title="Start typing to search and select a matching provider from your EMR.">
                                <InfoCircleOutlined style={{ color: '#1890ff', fontSize: 16, marginLeft: 4 }} />
                              </Tooltip>
                            )}
                          </span>
                        )
                      }
                    >
                      {this.state.apiCredentials ? (
                        <ReferringProviderAutocomplete
                          placeholder="Type to search providers"
                          name="authorizationReferringProvider"
                          value={this.state.authorizationReferringProvider}
                          onChange={(providerId, providerName) => this.setState({ authorizationReferringProvider: providerName })}
                        />
                      ) : (
                        <Input
                          placeholder="Referring provider"
                          value={this.state.authorizationReferringProvider}
                          onChange={(e) => this.setState({ authorizationReferringProvider: e.target.value })}
                        />
                      )}
                    </Form.Item>
                    <Form.Item
                      label="Note (optional)"
                    >
                      <Input
                        placeholder="Authorization note"
                        value={this.state.authorizationNote}
                        onChange={(e) => this.setState({ authorizationNote: e.target.value })}
                      />
                    </Form.Item>
                  </>
                )}
              </>
            )}
            {hasFeatureInLocalStorage(ARCHIVE_AFTER_RECORD_ACTIONS_FEATURE) && supportsArchiveCompletion && ['received_fax', 'ReceivedFax'].includes(receiverType) && (!this.state.createTaskAfterUpload || this.state.noTaskCreation) && (
              <Form.Item>
                <Checkbox
                  checked={this.state.archiveAfterUpload}
                  onChange={(event) => {
                    this.setState({ archiveAfterUpload: event.target.checked });
                    captureAnalytics('chart_archive_after_upload_toggled', { enabled: event.target.checked });
                  }}
                >
                  Archive after successful upload
                </Checkbox>
              </Form.Item>
            )}
          </Form>
        </ModalWrapper>
      </div>
    );
  }
}

AutoUpload.defaultProps = {
  allEmaTaskTemplates: {},
  allKareoTaskProviders: [],
  allMarketingRepresentatives: [],
};

AutoUpload.propTypes = {
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.object.isRequired,
  triggerAutoUpload: PropTypes.func.isRequired,
  triggerAutoUploadAndTaskCreation: PropTypes.func.isRequired,
  allDocumentCategories: PropTypes.object.isRequired,
  getDocumentCategories: PropTypes.func.isRequired,
  allEmaTaskProviders: PropTypes.array.isRequired,
  allKareoTaskProviders: PropTypes.array,
  getTaskTemplatesByQuery: PropTypes.func.isRequired,
  getEmaTaskProviders: PropTypes.func.isRequired,
  getKareoTaskProviders: PropTypes.func.isRequired,
  getEmaPatients: PropTypes.func.isRequired,
  getTebraPatients: PropTypes.func.isRequired,
  getMarketingRepresentatives: PropTypes.func.isRequired,
  apiPatients: PropTypes.object.isRequired,
  apiError: PropTypes.bool,
  apiErrorMessage: PropTypes.string,
  allEmaTaskTemplates: PropTypes.object,
  allMarketingRepresentatives: PropTypes.array,
  errorMessage: PropTypes.string,
  error: PropTypes.bool,
};

export default connect(state => ({
  allDocumentCategories: state.userProfile.allDocumentCategories,
  allEmaTaskTemplates: state.userProfile.allEmaTaskTemplates,
  allEmaTaskProviders: state.userProfile.allEmaTaskProviders,
  allKareoTaskProviders: state.userProfile.allKareoTaskProviders,
  allMarketingRepresentatives: state.userProfile.allMarketingRepresentatives,
  apiPatients: state.userProfile.apiPatients,
  apiError: state.userProfile.apiError,
  apiErrorMessage: state.userProfile.apiErrorMessage,
  error: state.records.error,
  errorMessage: state.records.errorMessage,
  apiCredentials: state.records.apiCredentials,
}))(AutoUpload);
